import React, { useEffect, useState } from "react";
import { Dumbbell } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";

import Layout from "src/components/app/layout/Layout";
import Header from "src/components/app/layout/Header";
import Button from "src/components/app/Button";
import ScheduleScreen from "src/components/app/schedule/ScheduleScreen";
import CoachProfile from "src/components/app/avatar/CoachProfile";

import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import {
  addAppointmentRequest,
  clearPlanPackageDurationRelId,
} from "src/redux/slices/appointmentsSlice";
import { formatDateInDDMMYYYY } from "src/helpers/common";
import { HealthCoachType } from "src/types/appointment.types";
import { useCheckForLatestConsultation } from "src/hooks/useCheckConsultation";
import { clearFinalAmountPaid } from "src/redux/slices/appointmentsSlice";
import { toast } from "sonner";
import { EVENT_NAMES, SCREEN_NAMES } from "src/helpers/analytics/events";
import analytics from "src/helpers/analytics";

const ScheduleAppointmentScreen: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const params = useParams();
  const supportProgramKey = params["support-program-key"];
  let IconComponent = Dumbbell;
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<string | null>(null);
  const coachKey = params["coach-key"];
  const coachData = useAppSelector((state) => state.appointments.coach);
  const [isBookingLoading, setIsBookingLoading] = useState(false);
  const finalAmountPaid = useAppSelector(
    (state) => state.appointments.finalAmountPaid
  );
  const { checkForLatestConsultation } = useCheckForLatestConsultation();
  const screenName = "BookConsultation";
  const analyticsData = {
    screen_name: `${screenName}_${SCREEN_NAMES.SLOT_SELECTION_SCREEN}`,
    support_program_key: supportProgramKey,
    selected_date: selectedDate,
    selected_time: selectedTimeSlot,
    ...coachData,
  };

  useEffect(() => {
    if (!coachData) {
      // fallback logic: either fetch specialist or redirect
      navigate(`/app/${supportProgramKey}/book-consultation`);
    } else {
      IconComponent = coachData?.icon;
    }
  }, []);

  if (!coachData) return null;

  const handleDateTimeChange = (
    selectedDate: Date | null,
    selectedTime: string | null
  ) => {
    setSelectedDate(selectedDate);
    setSelectedTimeSlot(selectedTime);
  };

  const addAppointment = async (
    plan_master_id?: string,
    plan_package_duration_rel_id?: string,
    patient_plan_rel_id?: string
  ) => {
    new Promise((resolve, reject) => {
      const formattedDate = formatDateInDDMMYYYY(selectedDate);
      dispatch(
        addAppointmentRequest({
          resolve,
          reject,
          payload: {
            appointment_date: formattedDate,
            consulation_type: "Video",
            health_coach_id: coachData?.health_coach_id,
            time_slot: selectedTimeSlot,
            type: "Coach",
            ...(plan_master_id && { planMasterId: plan_master_id }),
            ...(plan_package_duration_rel_id && {
              planPackageDurationRelId: plan_package_duration_rel_id,
            }),
            ...(patient_plan_rel_id && {
              patientPlanRelId: patient_plan_rel_id,
            }),
          },
        })
      );
    })
      .then((res: any) => {
        if (res?.code === "1" || res?.code === 1) {
          // Event: APPOINTMENT_BOOKING_SUCCESS
          analytics.trackEvent(
            EVENT_NAMES.APPOINTMENT_BOOKING_SUCCESS,
            analyticsData
          );
          toast.success("Appointment booked successfully");
        } else {
          // Event: APPOINTMENT_BOOKING_ERROR
          analytics.trackEvent(
            EVENT_NAMES.APPOINTMENT_BOOKING_ERROR,
            analyticsData
          );
          toast.error("Appointment booking failed");
        }
      })
      .catch(() => {
        // Event: APPOINTMENT_BOOKING_ERROR
        analytics.trackEvent(
          EVENT_NAMES.APPOINTMENT_BOOKING_ERROR,
          analyticsData
        );
        toast.error("Appointment booking failed");
      })
      .finally(() => {
        setIsBookingLoading(false);
        dispatch(clearFinalAmountPaid());
        dispatch(clearPlanPackageDurationRelId());
        navigate(`/app/${supportProgramKey}/book-consultation`);
      });
  };

  const handleConfirmBooking = async () => {
    // Event: APPOINTMENT_BOOKING_CLICKED
    analytics.trackEvent(
      EVENT_NAMES.APPOINTMENT_BOOKING_CLICKED,
      analyticsData
    );
    setIsBookingLoading(true);
    const { plan_master_id, plan_package_duration_rel_id } = coachData || {};
    if (coachData?.isPaid) {
      const latestConsulatationPlan = await checkForLatestConsultation(
        plan_master_id,
        plan_package_duration_rel_id
        //coachData?.isPaid,
        //coachKey as HealthCoachType
      );
      if (
        plan_master_id &&
        latestConsulatationPlan?.patient_plan_rel_id &&
        latestConsulatationPlan?.plan_package_duration_rel_id
      ) {
        addAppointment(
          plan_master_id,
          latestConsulatationPlan?.plan_package_duration_rel_id,
          latestConsulatationPlan?.patient_plan_rel_id
        );
      } else {
        toast.error("Unable to find the latest consultation plan");
      }
    } else {
      addAppointment();
    }
  };

  return (
    <Layout supportProgramKey={supportProgramKey || ""}>
      <div className="flex flex-col h-full">
        <Header
          title={`Book ${coachData?.role}`}
          onBackClick={() => {
            navigate(`/app/${supportProgramKey}/book-consultation`);
          }}
        />

        <ScheduleScreen
          onDateTimeChange={handleDateTimeChange}
          screenName="book_consultation"
        />

        {/* Booking Summary */}
        {selectedTimeSlot && (
          <div className="shadow-sm border border-gray-200 bg-gray-50 rounded-xl overflow-hidden">
            <div className="p-4">
              <div className="flex items-center gap-3 mb-3">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center overflow-hidden"
                  style={{ backgroundColor: "var(--icon-bg-primary)" }}
                >
                  <CoachProfile coachKey={coachKey as HealthCoachType} />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-800">
                    {coachData?.role}
                  </h4>
                  <p className="text-sm text-gray-600">
                    {selectedDate
                      ? `${selectedDate.toLocaleDateString("en-US", {
                          weekday: "short",
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        })} • ${selectedTimeSlot}`
                      : "No date selected"}
                  </p>
                </div>
              </div>
              <div className="flex items-center justify-between mb-4">
                {coachData?.isPaid ? (
                  <>
                    <span className="text-sm font-medium text-gray-700">
                      Total Amount Paid:
                    </span>
                    {finalAmountPaid < Number(coachData?.price) ? (
                      <span className="flex items-center gap-2">
                        <span className="line-through text-gray-500 text-sm">
                          ₹{coachData?.price}
                        </span>
                        <span
                          className="text-lg font-bold"
                          style={{ color: "var(--app-primary)" }}
                        >
                          ₹{finalAmountPaid}
                        </span>
                      </span>
                    ) : (
                      <span
                        className="text-lg font-bold"
                        style={{ color: "var(--app-primary)" }}
                      >
                        ₹{finalAmountPaid}
                      </span>
                    )}
                  </>
                ) : (
                  <span className="text-sm font-medium text-gray-700">
                    Total Amount: Free
                  </span>
                )}
              </div>
              <div className="flex justify-center">
                <Button
                  onClick={handleConfirmBooking}
                  fullWidth={true}
                  rounded="full"
                  loading={isBookingLoading}
                  disabled={isBookingLoading}
                >
                  Confirm Booking
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ScheduleAppointmentScreen;
